import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import { Typography, CircularProgress, Grid, TextField, Checkbox, FormControlLabel, FormControl, InputLabel, Select, MenuItem, Paper, Button } from "@material-ui/core";
import { ValidatorForm, TextValidator } from "react-material-ui-form-validator";
import { DatePicker, TimePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import MomentUtils from '@date-io/moment';
import moment from 'moment';
import {
    FormActions,
    FormButton,
    FormSubmitButton,
    Dialog,
    SectionCard,
    RadioListInput,
    SearchDropdown
} from "frame/components";
import { SiteForm } from "features/forms";
import ReadOnlyForm from "./components/read-only-form";
import { connect } from "react-redux";
import { safetyWiseApi } from "utils/safetyWiseApi";
import { onPagedListRequested as onSitesPagedListRequested } from "features/sites/actions";
import { onList as onStaffListRequest } from "features/staff/actions";
import { onSiteInspectionChecklistCreateRequest, onSiteInspectionChecklistFormReset, onSiteInspectionChecklistPagedListRequest } from "features/health-and-safety/actions";

const useStyles = makeStyles((theme) => ({
    root: {
        flex: 1,
        flexDirection: "row",
        padding: theme.spacing(3, 3, 10, 3),
    },
    error: {
        paddingTop: theme.spacing(1),
        color: theme.palette.error.main,
        paddingBottom: theme.spacing(7),
    },
    loading: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing(4)
    },
    loadingError: {
        textAlign: 'center',
        padding: theme.spacing(4),
        color: theme.palette.error.main
    },
    sectionTitle: {
        marginBottom: theme.spacing(2),
        fontWeight: 500
    },
    sectionCard: {
        marginBottom: theme.spacing(2)
    },
    checkboxContainer: {
        display: 'flex',
        flexDirection: 'column'
    },
    siteSelect: {
        width: '100%'
    },
    remedialActions: {
        padding: theme.spacing(2)
    }
}));

const EditSiteInspectionChecklistForm = ({ 
    id, 
    initialData,
    readOnly, 
    isClone, 
    onClose, 
    onSuccess,
    siteInspectionChecklists,
    currentOrganisationGUID,
    sites, 
    onGetSitesList, 
    staffList, 
    onGetStaffList, 
    dispatch,
    saving,
    savedSuccess,
    error 
}) => {
    const classes = useStyles();
    const [checklist, setChecklist] = useState(null);
    const [loading, setLoading] = useState(true);
    const [localError, setLocalError] = useState(null);

    // Options for Yes/No/NA radio buttons
    const options = [
        {
            label: "Yes",
            value: "yes",
        },
        {
            label: "No",
            value: "no",
        },
        {
            label: "NA",
            value: "na",
        },
    ];

    // Load site data when component mounts
    useEffect(() => {
        if (onGetSitesList) {
            onGetSitesList();
        }
    }, [onGetSitesList]);

    // Load staff data for the person responsible dropdown
    useEffect(() => {
        if (onGetStaffList) {
            onGetStaffList();
        }
    }, [onGetStaffList]);

    // Load real data based on the ID from Redux store
    useEffect(() => {
        if (!id) {
            setLocalError("No checklist ID provided");
            setLoading(false);
            return;
        }
        
        setLoading(true);
        setLocalError(null);
        
        console.log("Looking for checklist with ID:", id);
        
        // Use initialData first (e.g. from list page that uses local state instead of Redux)
        let data = initialData && initialData.id === id ? initialData : null;
        
        // Find the checklist in the Redux store data
        if (!data && siteInspectionChecklists && siteInspectionChecklists.listData) {
            data = siteInspectionChecklists.listData.find(item => item.id === id);
            console.log("Found checklist in Redux store:", !!data);
        }

        if (data) {
            setChecklist({ 
                ...data,
                save: { saving: false, success: false }
            });
            setLoading(false);
            return;
        }

        // Fetch directly from the API when not in Redux store
        if (currentOrganisationGUID) {
            safetyWiseApi
                .get(`${currentOrganisationGUID}/site-inspection-checklists/${id}`)
                .then(response => {
                    if (response && response.data) {
                        setChecklist({
                            ...response.data,
                            save: { saving: false, success: false }
                        });
                    } else {
                        setLocalError("Could not find the requested checklist. It may have been deleted or moved.");
                    }
                })
                .catch(() => {
                    setLocalError("Could not find the requested checklist. It may have been deleted or moved.");
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            setLocalError("Could not find the requested checklist. It may have been deleted or moved.");
            setLoading(false);
        }
    }, [id, initialData, isClone, siteInspectionChecklists]);

    // Handle save success
    useEffect(() => {
        if (savedSuccess) {
            console.log("Save successful, refreshing list and closing form");
            // Refresh the site inspection checklist list with proper parameters
            dispatch(onSiteInspectionChecklistPagedListRequest({ 
                pageNumber: 1, 
                pageSize: 50,
                timestamp: new Date().getTime()
            }));
            // Reset the save state and close the form
            dispatch(onSiteInspectionChecklistFormReset());
            // Call success callback to show message in parent
            if (onSuccess) {
                onSuccess("Site Inspection Checklist updated successfully");
            }
            onClose();
        }
    }, [savedSuccess, dispatch, onClose, onSuccess]);

    // Handle save error
    useEffect(() => {
        if (error) {
            console.error("Save failed:", error);
            setLocalError(error);
        }
    }, [error]);

    // Handle form value changes
    const handleChange = (field) => (event) => {
        const value = event.target.type === 'checkbox' 
            ? event.target.checked 
            : event.target.value;
            
        setChecklist({
            ...checklist,
            [field]: value
        });
    };

    // Handle Radio button changes
    const handleRadioChange = (field) => (value) => {
        setChecklist({
            ...checklist,
            [field]: value
        });
    };

    // Handle date changes for DatePicker components
    const handleDateChange = (field) => (momentValue) => {
        const dateValue = momentValue ? momentValue.format('YYYY-MM-DD') : '';
        setChecklist({
            ...checklist,
            [field]: dateValue
        });
    };

    // Handle remedial action changes
    const handleActionChange = (index, field) => (event) => {
        const value = field === 'completed' 
            ? event.target.checked 
            : event.target.value;
            
        const updatedActions = [...checklist.remedialActions];
        updatedActions[index] = {
            ...updatedActions[index],
            [field]: value
        };
        
        setChecklist({
            ...checklist,
            remedialActions: updatedActions
        });
    };

    // Helper function to convert string boolean to actual boolean
    const getCompletedValue = (completed) => {
        if (typeof completed === 'boolean') {
            return completed;
        }
        if (typeof completed === 'string') {
            return completed === 'true' || completed === 'True';
        }
        return false;
    };

    // Add a new remedial action
    const addRemedialAction = () => {
        // Only add one remedial action if none exist
        if (!checklist.remedialActions || checklist.remedialActions.length === 0) {
            const newAction = {
                action: "",
                comments: "",
                person: "",
                completed: false
            };
            
            setChecklist({
                ...checklist,
                remedialActions: [newAction]
            });
        }
    };

    // Remove a remedial action
    const removeRemedialAction = (index) => () => {
        const updatedActions = [...checklist.remedialActions];
        updatedActions.splice(index, 1);
        
        setChecklist({
            ...checklist,
            remedialActions: updatedActions
        });
    };

    // Handle form submission
    const handleSubmit = (event) => {
        event.preventDefault();
        
        // Clear any previous errors
        setLocalError(null);
        
        // Set current time if not already set
        const formData = { ...checklist };
        if (!formData.time) {
            formData.time = moment().format('HH:mm');
        }
        
        // Dispatch the Redux action to save the data
        console.log("Saving checklist:", formData);
        dispatch(onSiteInspectionChecklistCreateRequest(formData));
    };

    const onCancel = () => {
        onClose();
    };

    const open = true; // Always show dialog, loading state will be handled inside
    const title = readOnly
        ? "View Site Inspection Checklist"
        : "Edit Site Inspection Checklist";

    // Get available sites for dropdown
    const availableSites = sites && sites.results ? sites.results : [];

    // Handle site selection
    const handleSiteChange = (values) => {
        if (values && values.siteId) {
            setChecklist({
                ...checklist,
                siteId: values.siteId
            });
        }
    };

    // Render edit form
    const renderEditForm = () => (
        <>
            <SiteForm 
                values={{
                    siteId: checklist.siteId || '',
                    save: { saving: false },
                    isLoading: false,
                    createdAt: checklist.date ? new Date(checklist.date).toISOString().substring(0, 10) : new Date().toISOString().substring(0, 10)
                }}
                onChange={handleSiteChange}
                hideDate={true}
            />
            
            {/* Site Control */}
            <SectionCard title="1. Site Control" className={classes.sectionCard}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Hazard Board Signage
                        </Typography>
                        <RadioListInput
                            data-cy="hazardBoardSignage"
                            initialValue={checklist.hazardBoardSignage}
                            options={options}
                            onChange={handleRadioChange("hazardBoardSignage")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Environmental Plan Issues
                        </Typography>
                        <RadioListInput
                            data-cy="environmentalPlanIssues"
                            initialValue={checklist.environmentalPlanIssues}
                            options={options}
                            onChange={handleRadioChange("environmentalPlanIssues")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Toolbox Talk
                        </Typography>
                        <RadioListInput
                            data-cy="toolboxTalk"
                            initialValue={checklist.toolboxTalk}
                            options={options}
                            onChange={handleRadioChange("toolboxTalk")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <DatePicker
                            fullWidth
                            label="Toolbox Talk Date"
                            format="YYYY-MM-DD"
                            value={checklist.toolboxTalkDate ? moment(checklist.toolboxTalkDate) : null}
                            onChange={handleDateChange("toolboxTalkDate")}
                            inputVariant="outlined"
                            autoOk
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Safety Inductions
                        </Typography>
                        <RadioListInput
                            data-cy="safetyInductions"
                            initialValue={checklist.safetyInductions}
                            options={options}
                            onChange={handleRadioChange("safetyInductions")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Safety Notice Board
                        </Typography>
                        <RadioListInput
                            data-cy="safetyNoticeBoard"
                            initialValue={checklist.safetyNoticeBoard}
                            options={options}
                            onChange={handleRadioChange("safetyNoticeBoard")}
                        />
                    </Grid>
                </Grid>
            </SectionCard>

            {/* Site Facilities */}
            <SectionCard title="2. Site Facilities" className={classes.sectionCard}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Offices Clean
                        </Typography>
                        <RadioListInput
                            data-cy="officesClean"
                            initialValue={checklist.officesClean}
                            options={options}
                            onChange={handleRadioChange("officesClean")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Smoko Sheds Clean
                        </Typography>
                        <RadioListInput
                            data-cy="smokoShedsClean"
                            initialValue={checklist.smokoShedsClean}
                            options={options}
                            onChange={handleRadioChange("smokoShedsClean")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Toilets Clean
                        </Typography>
                        <RadioListInput
                            data-cy="toiletsClean"
                            initialValue={checklist.toiletsClean}
                            options={options}
                            onChange={handleRadioChange("toiletsClean")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Tool/Equipment Sheds Adequate
                        </Typography>
                        <RadioListInput
                            data-cy="toolEquipmentShedsAdequate"
                            initialValue={checklist.toolEquipmentShedsAdequate}
                            options={options}
                            onChange={handleRadioChange("toolEquipmentShedsAdequate")}
                        />
                    </Grid>
                </Grid>
            </SectionCard>

            {/* General Site Tidiness & Access Ways */}
            <SectionCard title="3. General Site Tidiness & Access Ways" className={classes.sectionCard}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Clear and Safe Access
                        </Typography>
                        <RadioListInput
                            data-cy="clearSafeAccess"
                            initialValue={checklist.clearSafeAccess}
                            options={options}
                            onChange={handleRadioChange("clearSafeAccess")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Stairways, Access Ways Clear
                        </Typography>
                        <RadioListInput
                            data-cy="stairwaysAccessWaysClear"
                            initialValue={checklist.stairwaysAccessWaysClear}
                            options={options}
                            onChange={handleRadioChange("stairwaysAccessWaysClear")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Hoardings and Boundary Fence
                        </Typography>
                        <RadioListInput
                            data-cy="hoardingsFenceSecure"
                            initialValue={checklist.hoardingsFenceSecure}
                            options={options}
                            onChange={handleRadioChange("hoardingsFenceSecure")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Loose Materials Secure
                        </Typography>
                        <RadioListInput
                            data-cy="looseMaterialsSecure"
                            initialValue={checklist.looseMaterialsSecure}
                            options={options}
                            onChange={handleRadioChange("looseMaterialsSecure")}
                        />
                    </Grid>
                </Grid>
            </SectionCard>

            {/* Personal Safety Equipment */}
            <SectionCard title="4. Personal Safety Equipment" className={classes.sectionCard}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Signage Displayed and Legible
                        </Typography>
                        <RadioListInput
                            data-cy="signageDisplayedLegible"
                            initialValue={checklist.signageDisplayedLegible}
                            options={options}
                            onChange={handleRadioChange("signageDisplayedLegible")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Hardhats Being Worn
                        </Typography>
                        <RadioListInput
                            data-cy="hardhatsWorn"
                            initialValue={checklist.hardhatsWorn}
                            options={options}
                            onChange={handleRadioChange("hardhatsWorn")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Correct Footwear
                        </Typography>
                        <RadioListInput
                            data-cy="correctFootwear"
                            initialValue={checklist.correctFootwear}
                            options={options}
                            onChange={handleRadioChange("correctFootwear")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Glasses/Ear Muffs/Vests/Masks
                        </Typography>
                        <RadioListInput
                            data-cy="glassesEarMuffsVestsMasks"
                            initialValue={checklist.glassesEarMuffsVestsMasks}
                            options={options}
                            onChange={handleRadioChange("glassesEarMuffsVestsMasks")}
                        />
                    </Grid>
                </Grid>
            </SectionCard>

            {/* First Aid / Fire Prevention */}
            <SectionCard title="5. First Aid / Fire Prevention" className={classes.sectionCard}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            First Aid Box Available
                        </Typography>
                        <RadioListInput
                            data-cy="firstAidBox"
                            initialValue={checklist.firstAidBox}
                            options={options}
                            onChange={handleRadioChange("firstAidBox")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Accident Register Available
                        </Typography>
                        <RadioListInput
                            data-cy="accidentRegister"
                            initialValue={checklist.accidentRegister}
                            options={options}
                            onChange={handleRadioChange("accidentRegister")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Fire Extinguishers Available
                        </Typography>
                        <RadioListInput
                            data-cy="fireExtinguishersAvailable"
                            initialValue={checklist.fireExtinguishersAvailable}
                            options={options}
                            onChange={handleRadioChange("fireExtinguishersAvailable")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Fire Extinguishers Current
                        </Typography>
                        <RadioListInput
                            data-cy="fireExtinguishersCurrent"
                            initialValue={checklist.fireExtinguishersCurrent}
                            options={options}
                            onChange={handleRadioChange("fireExtinguishersCurrent")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Fire Extinguishers Sufficient Number
                        </Typography>
                        <RadioListInput
                            data-cy="fireExtinguishersSufficientNumber"
                            initialValue={checklist.fireExtinguishersSufficientNumber}
                            options={options}
                            onChange={handleRadioChange("fireExtinguishersSufficientNumber")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Evacuation Procedure Current
                        </Typography>
                        <RadioListInput
                            data-cy="evacuationProcedureCurrent"
                            initialValue={checklist.evacuationProcedureCurrent}
                            options={options}
                            onChange={handleRadioChange("evacuationProcedureCurrent")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            All Emergencies Included
                        </Typography>
                        <RadioListInput
                            data-cy="allEmergenciesIncluded"
                            initialValue={checklist.allEmergenciesIncluded}
                            options={options}
                            onChange={handleRadioChange("allEmergenciesIncluded")}
                        />
                    </Grid>
                </Grid>
            </SectionCard>

            {/* Lifting Operations */}
            <SectionCard title="6. Lifting Operations" className={classes.sectionCard}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Lift Assessment and Plan
                        </Typography>
                        <RadioListInput
                            data-cy="liftAssessmentPlan"
                            initialValue={checklist.liftAssessmentPlan}
                            options={options}
                            onChange={handleRadioChange("liftAssessmentPlan")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Crane Certification Current
                        </Typography>
                        <RadioListInput
                            data-cy="craneCertificationCurrent"
                            initialValue={checklist.craneCertificationCurrent}
                            options={options}
                            onChange={handleRadioChange("craneCertificationCurrent")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Slings/Chains/Lifting Equipment Certified
                        </Typography>
                        <RadioListInput
                            data-cy="slingsChainsLifting"
                            initialValue={checklist.slingsChainsLifting}
                            options={options}
                            onChange={handleRadioChange("slingsChainsLifting")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Operator Procedures in Place
                        </Typography>
                        <RadioListInput
                            data-cy="operatorProcedures"
                            initialValue={checklist.operatorProcedures}
                            options={options}
                            onChange={handleRadioChange("operatorProcedures")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Inspections Done
                        </Typography>
                        <RadioListInput
                            data-cy="inspectionsDone"
                            initialValue={checklist.inspectionsDone}
                            options={options}
                            onChange={handleRadioChange("inspectionsDone")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Man Cage Available
                        </Typography>
                        <RadioListInput
                            data-cy="manCageAvailable"
                            initialValue={checklist.manCageAvailable}
                            options={options}
                            onChange={handleRadioChange("manCageAvailable")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Emergency Plan for Lifting
                        </Typography>
                        <RadioListInput
                            data-cy="emergencyPlanLifting"
                            initialValue={checklist.emergencyPlanLifting}
                            options={options}
                            onChange={handleRadioChange("emergencyPlanLifting")}
                        />
                    </Grid>
                </Grid>
            </SectionCard>

            {/* Compressed Air */}
            <SectionCard title="7. Compressed Air" className={classes.sectionCard}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Compressed Air in Good Condition
                        </Typography>
                        <RadioListInput
                            data-cy="compressedAirCondition"
                            initialValue={checklist.compressedAirCondition}
                            options={options}
                            onChange={handleRadioChange("compressedAirCondition")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Guards in Place
                        </Typography>
                        <RadioListInput
                            data-cy="compressedAirGuards"
                            initialValue={checklist.compressedAirGuards}
                            options={options}
                            onChange={handleRadioChange("compressedAirGuards")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Trained Users Only
                        </Typography>
                        <RadioListInput
                            data-cy="compressedAirTrainedUser"
                            initialValue={checklist.compressedAirTrainedUser}
                            options={options}
                            onChange={handleRadioChange("compressedAirTrainedUser")}
                        />
                    </Grid>
                </Grid>
            </SectionCard>

            {/* Excavations / Shoring */}
            <SectionCard title="8. Excavations / Shoring" className={classes.sectionCard}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Excavations Properly Shored
                        </Typography>
                        <RadioListInput
                            data-cy="excavationsShored"
                            initialValue={checklist.excavationsShored}
                            options={options}
                            onChange={handleRadioChange("excavationsShored")}
                        />
                    </Grid>
                </Grid>
            </SectionCard>

            {/* Hot Work / Welding */}
            <SectionCard title="9. Hot Work / Welding" className={classes.sectionCard}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Hot Work Permits
                        </Typography>
                        <RadioListInput
                            data-cy="hotWorkPermits"
                            initialValue={checklist.hotWorkPermits}
                            options={options}
                            onChange={handleRadioChange("hotWorkPermits")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Fire Extinguishers at Hand
                        </Typography>
                        <RadioListInput
                            data-cy="weldingFireExtinguishers"
                            initialValue={checklist.weldingFireExtinguishers}
                            options={options}
                            onChange={handleRadioChange("weldingFireExtinguishers")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Operators Using PPE
                        </Typography>
                        <RadioListInput
                            data-cy="weldingOperatorsPPE"
                            initialValue={checklist.weldingOperatorsPPE}
                            options={options}
                            onChange={handleRadioChange("weldingOperatorsPPE")}
                        />
                    </Grid>
                </Grid>
            </SectionCard>

            {/* Electrical Safety */}
            <SectionCard title="10. Electrical Safety" className={classes.sectionCard}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Main Board Lockable & Safe
                        </Typography>
                        <RadioListInput
                            data-cy="mainBoardLockable"
                            initialValue={checklist.mainBoardLockable}
                            options={options}
                            onChange={handleRadioChange("mainBoardLockable")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Current Tagged Leads
                        </Typography>
                        <RadioListInput
                            data-cy="currentTaggedLeads"
                            initialValue={checklist.currentTaggedLeads}
                            options={options}
                            onChange={handleRadioChange("currentTaggedLeads")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Current Tagged Plant
                        </Typography>
                        <RadioListInput
                            data-cy="currentTaggedPlant"
                            initialValue={checklist.currentTaggedPlant}
                            options={options}
                            onChange={handleRadioChange("currentTaggedPlant")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Current Tagged Lifeguards
                        </Typography>
                        <RadioListInput
                            data-cy="currentTaggedLifeguards"
                            initialValue={checklist.currentTaggedLifeguards}
                            options={options}
                            onChange={handleRadioChange("currentTaggedLifeguards")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Leads Safely Placed
                        </Typography>
                        <RadioListInput
                            data-cy="leadsSafelyPlaced"
                            initialValue={checklist.leadsSafelyPlaced}
                            options={options}
                            onChange={handleRadioChange("leadsSafelyPlaced")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Equipment in Good Condition
                        </Typography>
                        <RadioListInput
                            data-cy="equipmentGoodCondition"
                            initialValue={checklist.equipmentGoodCondition}
                            options={options}
                            onChange={handleRadioChange("equipmentGoodCondition")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Appropriate Guards on Equipment
                        </Typography>
                        <RadioListInput
                            data-cy="appropriateGuardsEquipment"
                            initialValue={checklist.appropriateGuardsEquipment}
                            options={options}
                            onChange={handleRadioChange("appropriateGuardsEquipment")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Adequate Temporary Lighting
                        </Typography>
                        <RadioListInput
                            data-cy="adequateTemporaryLighting"
                            initialValue={checklist.adequateTemporaryLighting}
                            options={options}
                            onChange={handleRadioChange("adequateTemporaryLighting")}
                        />
                    </Grid>
                </Grid>
            </SectionCard>

            {/* Chemical & Hazardous Substances */}
            <SectionCard title="11. Chemical & Hazardous Substances" className={classes.sectionCard}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Chemicals Correctly Stored
                        </Typography>
                        <RadioListInput
                            data-cy="chemicalsCorrectlyStored"
                            initialValue={checklist.chemicalsCorrectlyStored}
                            options={options}
                            onChange={handleRadioChange("chemicalsCorrectlyStored")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            SDS Available
                        </Typography>
                        <RadioListInput
                            data-cy="sdsAvailable"
                            initialValue={checklist.sdsAvailable}
                            options={options}
                            onChange={handleRadioChange("sdsAvailable")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Operators Using PPE
                        </Typography>
                        <RadioListInput
                            data-cy="chemicalOperatorsPPE"
                            initialValue={checklist.chemicalOperatorsPPE}
                            options={options}
                            onChange={handleRadioChange("chemicalOperatorsPPE")}
                        />
                    </Grid>
                </Grid>
            </SectionCard>

            {/* Tools & Task Analysis */}
            <SectionCard title="12. Tools & Task Analysis" className={classes.sectionCard}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            PAT / TOOL WOF (12mth)
                        </Typography>
                        <RadioListInput
                            data-cy="patToolWof"
                            initialValue={checklist.patToolWof}
                            options={options}
                            onChange={handleRadioChange("patToolWof")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Staff Trained to Use Tools
                        </Typography>
                        <RadioListInput
                            data-cy="staffTrainedTools"
                            initialValue={checklist.staffTrainedTools}
                            options={options}
                            onChange={handleRadioChange("staffTrainedTools")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            PAT Signage & Job Safety Sheets
                        </Typography>
                        <RadioListInput
                            data-cy="patSignage"
                            initialValue={checklist.patSignage}
                            options={options}
                            onChange={handleRadioChange("patSignage")}
                        />
                    </Grid>
                </Grid>
            </SectionCard>

            {/* Scaffolding / Work Platforms */}
            <SectionCard title="13. Scaffolding / Work Platforms" className={classes.sectionCard}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Notifiable / Weekly Inspections Done
                        </Typography>
                        <RadioListInput
                            data-cy="scaffoldingNotifiableWeekly"
                            initialValue={checklist.scaffoldingNotifiableWeekly}
                            options={options}
                            onChange={handleRadioChange("scaffoldingNotifiableWeekly")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Handrails
                        </Typography>
                        <RadioListInput
                            data-cy="scaffoldingHandrails"
                            initialValue={checklist.scaffoldingHandrails}
                            options={options}
                            onChange={handleRadioChange("scaffoldingHandrails")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Toe Boards
                        </Typography>
                        <RadioListInput
                            data-cy="scaffoldingToeBoards"
                            initialValue={checklist.scaffoldingToeBoards}
                            options={options}
                            onChange={handleRadioChange("scaffoldingToeBoards")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Platforms Sound
                        </Typography>
                        <RadioListInput
                            data-cy="scaffoldingPlatforms"
                            initialValue={checklist.scaffoldingPlatforms}
                            options={options}
                            onChange={handleRadioChange("scaffoldingPlatforms")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Ladders Tied & 1m Overhang
                        </Typography>
                        <RadioListInput
                            data-cy="scaffoldingLadders"
                            initialValue={checklist.scaffoldingLadders}
                            options={options}
                            onChange={handleRadioChange("scaffoldingLadders")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Base Sound and Level
                        </Typography>
                        <RadioListInput
                            data-cy="scaffoldingBaseSound"
                            initialValue={checklist.scaffoldingBaseSound}
                            options={options}
                            onChange={handleRadioChange("scaffoldingBaseSound")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Work Platforms Clear & Safe
                        </Typography>
                        <RadioListInput
                            data-cy="scaffoldingWorkPlatformsClear"
                            initialValue={checklist.scaffoldingWorkPlatformsClear}
                            options={options}
                            onChange={handleRadioChange("scaffoldingWorkPlatformsClear")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Platforms Trip Free
                        </Typography>
                        <RadioListInput
                            data-cy="scaffoldingPlatformsTripFree"
                            initialValue={checklist.scaffoldingPlatformsTripFree}
                            options={options}
                            onChange={handleRadioChange("scaffoldingPlatformsTripFree")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Planks Tied
                        </Typography>
                        <RadioListInput
                            data-cy="scaffoldingPlanksTied"
                            initialValue={checklist.scaffoldingPlanksTied}
                            options={options}
                            onChange={handleRadioChange("scaffoldingPlanksTied")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Headroom Clear
                        </Typography>
                        <RadioListInput
                            data-cy="scaffoldingHeadroomClear"
                            initialValue={checklist.scaffoldingHeadroomClear}
                            options={options}
                            onChange={handleRadioChange("scaffoldingHeadroomClear")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Ties / Bracing
                        </Typography>
                        <RadioListInput
                            data-cy="scaffoldingTiesBracing"
                            initialValue={checklist.scaffoldingTiesBracing}
                            options={options}
                            onChange={handleRadioChange("scaffoldingTiesBracing")}
                        />
                    </Grid>
                </Grid>
            </SectionCard>

            {/* Ladder Use */}
            <SectionCard title="14. Ladder Use" className={classes.sectionCard}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Ladders in Good Condition
                        </Typography>
                        <RadioListInput
                            data-cy="laddersGoodCondition"
                            initialValue={checklist.laddersGoodCondition}
                            options={options}
                            onChange={handleRadioChange("laddersGoodCondition")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Ladders Secured
                        </Typography>
                        <RadioListInput
                            data-cy="laddersSecured"
                            initialValue={checklist.laddersSecured}
                            options={options}
                            onChange={handleRadioChange("laddersSecured")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Stays
                        </Typography>
                        <RadioListInput
                            data-cy="laddersStays"
                            initialValue={checklist.laddersStays}
                            options={options}
                            onChange={handleRadioChange("laddersStays")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Working 3 Steps Down on Ladder
                        </Typography>
                        <RadioListInput
                            data-cy="laddersWorkingStepsDown"
                            initialValue={checklist.laddersWorkingStepsDown}
                            options={options}
                            onChange={handleRadioChange("laddersWorkingStepsDown")}
                        />
                    </Grid>
                </Grid>
            </SectionCard>

            {/* Working at Height / Fall Hazards */}
            <SectionCard title="15. Working at Height / Fall Hazards" className={classes.sectionCard}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Floor Edges, Holes, Open Areas Protected
                        </Typography>
                        <RadioListInput
                            data-cy="fallHazardsFloorEdges"
                            initialValue={checklist.fallHazardsFloorEdges}
                            options={options}
                            onChange={handleRadioChange("fallHazardsFloorEdges")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="left" gutterBottom variant="body1">
                            Lift Shafts, Pits Protected / Access Controlled
                        </Typography>
                        <RadioListInput
                            data-cy="fallHazardsLiftShafts"
                            initialValue={checklist.fallHazardsLiftShafts}
                            options={options}
                            onChange={handleRadioChange("fallHazardsLiftShafts")}
                        />
                    </Grid>
                </Grid>
            </SectionCard>

            {/* Remedial Actions */}
            <SectionCard title="Remedial Actions" className={classes.sectionCard}>
                {(checklist.remedialActions || []).map((action, index) => (
                    <Paper key={index} className={classes.remedialActions} style={{ marginBottom: '16px', padding: '16px' }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextValidator
                                    fullWidth
                                    label="Action"
                                    variant="outlined"
                                    value={action.action || ""}
                                    onChange={handleActionChange(index, "action")}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextValidator
                                    fullWidth
                                    label="Comments"
                                    variant="outlined"
                                    value={action.comments || ""}
                                    onChange={handleActionChange(index, "comments")}
                                    multiline
                                    rows={2}
                                />
                            </Grid>
                            <Grid item xs={12} md={8}>
                                <SearchDropdown
                                    id={`person-responsible-${index}`}
                                    label="Person Responsible"
                                    value={action.person || ""}
                                    onChange={(value) => {
                                        const updatedActions = [...checklist.remedialActions];
                                        updatedActions[index] = {
                                            ...updatedActions[index],
                                            person: value
                                        };
                                        
                                        setChecklist({
                                            ...checklist,
                                            remedialActions: updatedActions
                                        });
                                    }}
                                    defaultOptions={staffList && staffList.map(staff => ({
                                        label: `${staff.firstName} ${staff.lastName || ''}`.trim(),
                                        value: `${staff.firstName} ${staff.lastName || ''}`.trim()
                                    }))}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={getCompletedValue(action.completed)}
                                            onChange={handleActionChange(index, "completed")}
                                            color="primary"
                                        />
                                    }
                                    label="Completed"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={removeRemedialAction(index)}
                                >
                                    Remove
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>
                ))}
                <Button
                    variant="contained"
                    color="primary"
                    onClick={addRemedialAction}
                    style={{ marginTop: '8px' }}
                >
                    Add Remedial Action
                </Button>
            </SectionCard>
        </>
    );

    return (
        <MuiPickersUtilsProvider utils={MomentUtils}>
            <Dialog 
                open={open}
                aria-labelledby="Site Inspection Checklist"
                title={title}
                onClose={onCancel}
                maxWidth="md"
                fullWidth
            >
                {loading ? (
                    <div className={classes.loading}>
                        <CircularProgress />
                    </div>
                ) : localError ? (
                    <div className={classes.loadingError}>
                        <Typography variant="body1">{localError}</Typography>
                        <FormButton onClick={onCancel} style={{ marginTop: '16px' }}>
                            Close
                        </FormButton>
                    </div>
                ) : checklist ? (
                    <ValidatorForm autoComplete="off" onSubmit={handleSubmit}>
                        {readOnly ? (
                            <ReadOnlyForm values={checklist} />
                        ) : (
                            renderEditForm()
                        )}
                        <FormActions className={classes.actions}>
                            {!readOnly && (
                                <FormSubmitButton
                                    disabled={saving}
                                    loading={saving}
                                >
                                    Save
                                </FormSubmitButton>
                            )}
                            <FormButton
                                disabled={saving}
                                onClick={onCancel}
                            >
                                {readOnly ? "Close" : "Cancel"}
                            </FormButton>
                        </FormActions>
                        {(localError || error) && (
                            <Typography variant="body1" className={classes.error}>
                                {localError || error}
                            </Typography>
                        )}
                    </ValidatorForm>
                ) : (
                    <div className={classes.loadingError}>
                        <Typography variant="body1">No checklist data available.</Typography>
                        <FormButton onClick={onCancel} style={{ marginTop: '16px' }}>
                            Close
                        </FormButton>
                    </div>
                )}
            </Dialog>
        </MuiPickersUtilsProvider>
    );
};

EditSiteInspectionChecklistForm.propTypes = {
    id: PropTypes.string.isRequired,
    initialData: PropTypes.object,
    readOnly: PropTypes.bool,
    isClone: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func,
    siteInspectionChecklists: PropTypes.object,
    currentOrganisationGUID: PropTypes.string,
    sites: PropTypes.object,
    onGetSitesList: PropTypes.func,
    staffList: PropTypes.array,
    onGetStaffList: PropTypes.func,
    dispatch: PropTypes.func,
    saving: PropTypes.bool,
    savedSuccess: PropTypes.bool,
    error: PropTypes.string
};

const mapStateToProps = (state) => ({
    siteInspectionChecklists: state.healthSafety.siteInspectionChecklists,
    currentOrganisationGUID: state.organisations && state.organisations.currentOrganisation && (state.organisations.currentOrganisation.id || state.organisations.currentOrganisation),
    sites: state.sites.pagination,
    staffList: state.staff && state.staff.pagination && state.staff.pagination.results,
    saving: state.healthSafety && state.healthSafety.saving,
    savedSuccess: state.healthSafety && state.healthSafety.savedSuccess,
    error: state.healthSafety && state.healthSafety.error
});

const mapDispatchToProps = (dispatch) => ({
    onGetSitesList: () => dispatch(onSitesPagedListRequested({ pageNumber: 1, pageSize: 1000 })),
    onGetStaffList: () => dispatch(onStaffListRequest()),
    dispatch: dispatch
});

export default connect(mapStateToProps, mapDispatchToProps)(EditSiteInspectionChecklistForm); 